import { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import RedoIcon from '@mui/icons-material/Redo';
import RemoveIcon from '@mui/icons-material/Remove';
import UndoIcon from '@mui/icons-material/Undo';
import { MenuItem, Select, useMediaQuery, useTheme } from '@mui/material';
import { Editor } from '@tiptap/react';
import { TipTapBarButton } from './components/TipTapBarButton';
import { TipTapBarContainer } from './components/TipTapBarContainer';
import { TipTapBarDivider } from './components/TipTapBarDivider';

const VISIBLE_BUTTONS_COUNT = 3;

interface TipTapBarProps {
  editor: Editor;
}
/**
 * if things get more complicated, split each functionality into separate components.
 * And move to /tip-tap-bar/components.
 * Shared components accross editor move to TipTapEditor/components.
 */
const TipTapBar = ({ editor }: TipTapBarProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isToolbarExpanded, setIsToolbarExpanded] = useState<boolean>(false);

  const allButtons = [
    <TipTapBarButton
      key="undo"
      size="small"
      onClick={() => editor.chain().focus().undo().run()}
      disabled={!editor.can().undo()}
    >
      <UndoIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <TipTapBarButton
      key="redo"
      size="small"
      onClick={() => editor.chain().focus().redo().run()}
      disabled={!editor.can().redo()}
      sx={{ padding: '2px' }}
    >
      <RedoIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <div key="heading-group">
      <TipTapBarDivider />
      <Select
        size="small"
        value={editor.getAttributes('heading')?.level || 'paragraph'}
        onChange={(e) => {
          const level = e.target.value;
          if (level === 'paragraph') {
            editor.chain().focus().setParagraph().run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: Number(level) as 1 | 2 | 3 })
              .run();
          }
        }}
        sx={{
          minWidth: '100px',
          height: '24px',
          '.MuiSelect-select': {
            py: 0,
            lineHeight: '24px',
          },
          fontSize: 14,
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              '& .MuiMenuItem-root': {
                fontSize: 14,
              },
            },
          },
        }}
        variant="standard"
      >
        <MenuItem value="paragraph">Paragraph</MenuItem>
        <MenuItem value={1}>Heading 1</MenuItem>
        <MenuItem value={2}>Heading 2</MenuItem>
        <MenuItem value={3}>Heading 3</MenuItem>
      </Select>
      <TipTapBarDivider />
    </div>,
    <TipTapBarButton
      key="bold"
      size="small"
      onClick={() => editor.chain().focus().toggleBold().run()}
      color={editor.isActive('bold') ? 'primary' : 'default'}
      sx={{ padding: '2px' }}
    >
      <FormatBoldIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <TipTapBarButton
      key="italic"
      size="small"
      onClick={() => editor.chain().focus().toggleItalic().run()}
      color={editor.isActive('italic') ? 'primary' : 'default'}
      sx={{ padding: '2px' }}
    >
      <FormatItalicIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <TipTapBarButton
      key="link"
      size="small"
      onClick={() => {
        const url = window.prompt('Enter URL');
        if (url) {
          editor.chain().focus().setLink({ href: url }).run();
        }
      }}
      color={editor.isActive('link') ? 'primary' : 'default'}
      sx={{ padding: '2px' }}
    >
      <LinkIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <div key="list-group">
      <TipTapBarDivider />
      <TipTapBarButton
        size="small"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        color={editor.isActive('bulletList') ? 'primary' : 'default'}
        sx={{ padding: '2px' }}
      >
        <FormatListBulletedIcon sx={{ fontSize: 18 }} />
      </TipTapBarButton>
      <TipTapBarButton
        size="small"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        color={editor.isActive('orderedList') ? 'primary' : 'default'}
        sx={{ padding: '2px' }}
      >
        <FormatListNumberedIcon sx={{ fontSize: 18 }} />
      </TipTapBarButton>
      <TipTapBarDivider />
    </div>,
    <TipTapBarButton
      key="align-left"
      size="small"
      onClick={() => editor.chain().focus().setTextAlign('left').run()}
      color={editor.isActive({ textAlign: 'left' }) ? 'primary' : 'default'}
      sx={{ padding: '2px' }}
    >
      <FormatAlignLeftIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <TipTapBarButton
      key="align-center"
      size="small"
      onClick={() => editor.chain().focus().setTextAlign('center').run()}
      color={editor.isActive({ textAlign: 'center' }) ? 'primary' : 'default'}
      sx={{ padding: '2px' }}
    >
      <FormatAlignCenterIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <TipTapBarButton
      key="align-right"
      size="small"
      onClick={() => editor.chain().focus().setTextAlign('right').run()}
      color={editor.isActive({ textAlign: 'right' }) ? 'primary' : 'default'}
      sx={{ padding: '2px' }}
    >
      <FormatAlignRightIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <TipTapBarButton
      key="align-justify"
      size="small"
      onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      color={editor.isActive({ textAlign: 'justify' }) ? 'primary' : 'default'}
      sx={{ padding: '2px' }}
    >
      <FormatAlignJustifyIcon sx={{ fontSize: 18 }} />
    </TipTapBarButton>,
    <div key="blockquote-group">
      <TipTapBarDivider />
      <TipTapBarButton
        size="small"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        color={editor.isActive('blockquote') ? 'primary' : 'default'}
        sx={{ padding: '2px' }}
      >
        <FormatQuoteIcon sx={{ fontSize: 18 }} />
      </TipTapBarButton>
    </div>,
  ];

  const visibleButtons = allButtons.slice(0, VISIBLE_BUTTONS_COUNT);
  const extraButtons = allButtons.slice(VISIBLE_BUTTONS_COUNT);

  if (isMobile)
    return (
      <>
        <TipTapBarContainer
          sx={{
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            ...(!isToolbarExpanded && {
              borderBottom: '1px solid',
              borderColor: 'divider',
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
            }),
          }}
        >
          {visibleButtons}

          <TipTapBarButton
            onClick={() => setIsToolbarExpanded((prev) => !prev)}
            size="small"
            sx={{ ml: 'auto' }}
          >
            {!isToolbarExpanded ? <AddIcon /> : <RemoveIcon />}
          </TipTapBarButton>
        </TipTapBarContainer>

        {isToolbarExpanded && (
          <TipTapBarContainer
            sx={{
              flexWrap: 'wrap',
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {extraButtons}
          </TipTapBarContainer>
        )}
      </>
    );

  return (
    <TipTapBarContainer
      sx={{
        borderBottom: '1px solid',
        borderRadius: '4px',
        borderColor: 'divider',
      }}
    >
      {allButtons}
    </TipTapBarContainer>
  );
};

export default TipTapBar;
